/**
 * Profile API hooks (AK-229 drawer + AK-230 password reset).
 *
 * Backend contract — backend/src/ikolhoz/api/v1/profile.py + auth.py + twofa.py:
 *   GET    /api/v1/profile/me              — ProfileMeOut (UserOut + tenants + 2fa + sessions_count)
 *   PATCH  /api/v1/profile/me              — ФИО / phone / lang / tz
 *   POST   /api/v1/profile/me/email/request — change email (требует current_password)
 *   POST   /api/v1/profile/me/password     — change password (current + new)
 *   GET    /api/v1/profile/me/sessions     — SessionsListOut
 *   DELETE /api/v1/profile/me/sessions/{id} — revoke one
 *   GET    /api/v1/auth/2fa/status         — TwoFAStatusOut
 *   POST   /api/v1/auth/2fa/setup          — TwoFASetupOut (qr_data_url + secret)
 *   POST   /api/v1/auth/2fa/verify-setup   — { code } → TwoFAVerifySetupOut (recovery_codes)
 *   POST   /api/v1/auth/2fa/disable        — { code } → 204
 *   POST   /api/v1/auth/password-reset/request — { email } → StatusOut (всегда 200)
 *   POST   /api/v1/auth/password-reset/confirm — { token, new_password } → StatusOut
 *
 * TanStack Query keys — flat, чтобы invalidate ['profile'] чистил всё разом.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Типы (mirror Pydantic) ───────────────────────────────────────────────

export interface ProfileTenant {
  id: string;
  slug: string;
  name: string;
  role: string;
  is_active: boolean;
}

export interface ProfileMe {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  display_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  preferred_language: string;
  timezone: string;
  is_platform_admin: boolean;
  platform_role: string | null;
  tenants: ProfileTenant[];
  two_factor_enabled: boolean;
  sessions_count: number;
  /**
   * У пользователя задан пароль. `false` — вошёл по приглашению/magic-link;
   * раздел «Пароль» тогда переключается в режим установки первого пароля
   * (поля «текущий» нет — вводить нечего).
   */
  has_password: boolean;
}

export interface ProfileUpdatePayload {
  first_name: string;
  last_name: string;
  middle_name?: string | null;
  phone?: string | null;
  preferred_language?: string;
  timezone?: string;
}

export interface ProfileSession {
  id: string;
  ip: string | null;
  user_agent: string | null;
  browser: string | null;
  os: string | null;
  device_name: string | null;
  created_at: string;
  last_active_at: string;
  is_current_session: boolean;
}

export interface ProfileSessionsList {
  items: ProfileSession[];
  total: number;
}

export interface TwoFAStatus {
  enabled: boolean;
  has_secret: boolean;
  recovery_codes_left: number;
}

export interface TwoFASetup {
  secret: string;
  qr_data_url: string;
  provisioning_uri: string;
}

export interface TwoFAVerifySetupOut {
  enabled: boolean;
  recovery_codes: string[];
}

// ─── Queries ──────────────────────────────────────────────────────────────

export const profileKeys = {
  me: ['profile', 'me'] as const,
  sessions: ['profile', 'sessions'] as const,
  twofaStatus: ['profile', '2fa', 'status'] as const,
};

/** GET /profile/me — расширенный профиль для drawer'а. */
export function useProfileMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: profileKeys.me,
    queryFn: () => api.get<ProfileMe>('/api/v1/profile/me'),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** GET /profile/me/sessions — активные сессии. */
export function useProfileSessions(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: profileKeys.sessions,
    queryFn: () => api.get<ProfileSessionsList>('/api/v1/profile/me/sessions'),
    staleTime: 15_000,
    enabled: options?.enabled ?? true,
  });
}

/** GET /auth/2fa/status — состояние 2FA. */
export function useTwoFAStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: profileKeys.twofaStatus,
    queryFn: () => api.get<TwoFAStatus>('/api/v1/auth/2fa/status'),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileUpdatePayload) =>
      api.patch('/api/v1/profile/me', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.me });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: { current_password: string; new_password: string }) =>
      api.post<void>('/api/v1/profile/me/password', payload),
  });
}

export function useRequestEmailChange() {
  return useMutation({
    mutationFn: (payload: { new_email: string; current_password: string }) =>
      api.post('/api/v1/profile/me/email/request', payload),
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.delete(`/api/v1/profile/me/sessions/${sessionId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.sessions });
      void qc.invalidateQueries({ queryKey: profileKeys.me });
    },
  });
}

export function useStart2FASetup() {
  return useMutation({
    mutationFn: () => api.post<TwoFASetup>('/api/v1/auth/2fa/setup'),
  });
}

export function useVerify2FASetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { code: string }) =>
      api.post<TwoFAVerifySetupOut>('/api/v1/auth/2fa/verify-setup', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.twofaStatus });
      void qc.invalidateQueries({ queryKey: profileKeys.me });
    },
  });
}

export function useDisable2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { code: string }) =>
      api.post<void>('/api/v1/auth/2fa/disable', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.twofaStatus });
      void qc.invalidateQueries({ queryKey: profileKeys.me });
    },
  });
}

// ─── Password reset (AK-230, public — без current_user) ───────────────────

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (payload: { email: string }) =>
      api.post('/api/v1/auth/password-reset/request', payload),
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (payload: { token: string; new_password: string }) =>
      api.post('/api/v1/auth/password-reset/confirm', payload),
  });
}

/**
 * Установить пароль при первом входе по приглашению.
 *
 * После accept-invite новый user auto-залогинен (session-cookie), но у него
 * ещё нет password_hash. Этот хук дёргает существующий /auth/set-password
 * (current_user_required — по session-cookie), чтобы user смог войти повторно.
 * Пароль передаётся только в теле POST по HTTPS.
 */
export function useSetInitialPassword() {
  return useMutation({
    mutationFn: (payload: { password: string }) =>
      api.post<void>('/api/v1/auth/set-password', payload),
  });
}
